import { useLoaderData, useFetcher, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useEffect } from "react";
import { Header } from "../../components/layout/Header";
import { MemberList } from "../../components/organizations/MemberList";
import { LinkButton } from "../../components/common";
import type { Member } from "../../components/organizations/MemberList";
import type { OrganizationDetailData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import { useAuth } from "../../hooks/useAuth";
import type { Role } from "../../types/user";
import styles from "./OrganizationDetailPage.module.css";

export default function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { organization, members, teams, podcasts } = useLoaderData() as OrganizationDetailData;
  const { user } = useAuth();
  const roleChangeFetcher = useFetcher<ActionResult>();
  const removeMemberFetcher = useFetcher<ActionResult>();

  const userRole = user?.organizations.find((o) => o.id === id)?.role ?? null;
  const canManageOrg = userRole === "owner" || userRole === "admin";

  const transformedMembers: Member[] = members.map((m) => ({
    id: m.userId,
    email: m.email,
    emailVerified: m.emailVerified,
    role: m.role,
    joinedAt: m.joinedAt,
  }));

  useEffect(() => {
    if (roleChangeFetcher.data && roleChangeFetcher.state === "idle") {
      if (roleChangeFetcher.data.success) {
        toast.success("Role updated successfully");
      } else if (roleChangeFetcher.data.error) {
        toast.error(roleChangeFetcher.data.error);
      }
    }
  }, [roleChangeFetcher.data, roleChangeFetcher.state]);

  useEffect(() => {
    if (removeMemberFetcher.data && removeMemberFetcher.state === "idle") {
      if (removeMemberFetcher.data.success) {
        toast.success("Member removed successfully");
      } else if (removeMemberFetcher.data.error) {
        toast.error(removeMemberFetcher.data.error);
      }
    }
  }, [removeMemberFetcher.data, removeMemberFetcher.state]);

  const handleRoleChange = async (userId: string, newRole: Role) => {
    roleChangeFetcher.submit(
      { role: newRole },
      {
        method: "post",
        action: `/organizations/${id}/members/${userId}/role`,
      },
    );
  };

  const handleRemoveMember = async (userId: string) => {
    if (!confirm("Are you sure you want to remove this member?")) return;

    removeMemberFetcher.submit(null, {
      method: "post",
      action: `/organizations/${id}/members/${userId}/remove`,
    });
  };

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.header}>
          <div>
            <h1>{organization.name}</h1>
            <p className={styles.slug}>@{organization.slug}</p>
          </div>
          {userRole === "owner" && (
            <div className={styles.headerActions}>
              <LinkButton to={`/organizations/${id}/billing`} variant="secondary">
                Billing
              </LinkButton>
              <LinkButton to={`/organizations/${id}/settings`} variant="secondary">
                Settings
              </LinkButton>
            </div>
          )}
          {userRole === "admin" && (
            <LinkButton to={`/organizations/${id}/settings`} variant="secondary">
              Settings
            </LinkButton>
          )}
        </div>

        <div className={styles.grid}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Podcasts ({podcasts.length})</h2>
              {canManageOrg && (
                <LinkButton
                  to={`/organizations/${id}/podcasts/create`}
                  variant="primary"
                  size="sm"
                >
                  Create Podcast
                </LinkButton>
              )}
            </div>
            <div>
              {podcasts.length === 0 ? (
                <p className={styles.emptyMessage}>
                  No podcasts yet. Create your first podcast to get started!
                </p>
              ) : (
                <div className={styles.podcastsGrid}>
                  {podcasts.map((podcast) => (
                    <Link
                      key={podcast.id}
                      to={`/organizations/${id}/podcasts/${podcast.id}`}
                      className={styles.podcastCard}
                    >
                      <h3>{podcast.name}</h3>
                      {podcast.description && (
                        <p className={styles.podcastDescription}>{podcast.description}</p>
                      )}
                      <p className={styles.podcastMeta}>
                        {podcast.category || "Uncategorized"} · {podcast.language.toUpperCase()}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Members ({transformedMembers.length})</h2>
              {canManageOrg && (
                <LinkButton to={`/organizations/${id}/members/invite`} variant="primary" size="sm">
                  Invite Member
                </LinkButton>
              )}
            </div>
            <div className="members-list">
              {transformedMembers.length === 0 ? (
                <p className={styles.emptyMessage}>No members yet</p>
              ) : (
                <MemberList
                  members={transformedMembers}
                  currentUserRole={userRole || "member"}
                  organizationCreatorId={organization.createdBy}
                  onRoleChange={handleRoleChange}
                  onRemoveMember={handleRemoveMember}
                />
              )}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Teams ({teams.length})</h2>
              {canManageOrg && (
                <LinkButton to={`/organizations/${id}/teams/create`} variant="primary" size="sm">
                  Create Team
                </LinkButton>
              )}
            </div>
            <div className="teams-list">
              {teams.length === 0 ? (
                <p className={styles.emptyMessage}>No teams yet</p>
              ) : (
                <div className={styles.teamsGrid}>
                  {teams.map((team) => (
                    <Link
                      key={team.id}
                      to={`/organizations/${id}/teams/${team.id}`}
                      className={styles.teamCard}
                    >
                      <h3>{team.name}</h3>
                      {team.description && (
                        <p className={styles.teamDescription}>{team.description}</p>
                      )}
                      <p className={styles.teamMeta}>
                        Created {new Date(team.createdAt).toLocaleDateString()}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Networks</h2>
              {canManageOrg && (
                <LinkButton to={`/organizations/${id}/networks`} variant="primary" size="sm">
                  Manage Networks
                </LinkButton>
              )}
            </div>
            <p className={styles.emptyMessage}>
              Organize your podcasts into networks for cross-promotion and shared revenue.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
